import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const SEM_META = [
  { n: 1, label: "Semester 1", kelas: "VII Ganjil" },
  { n: 2, label: "Semester 2", kelas: "VII Genap" },
  { n: 3, label: "Semester 3", kelas: "VIII Ganjil" },
  { n: 4, label: "Semester 4", kelas: "VIII Genap" },
  { n: 5, label: "Semester 5", kelas: "IX Ganjil" },
  { n: 6, label: "Semester 6 + Kelulusan", kelas: "IX Genap" },
];

const P5_DIMENSIONS = [
  "Beriman, Bertakwa kepada Tuhan YME dan Berakhlak Mulia",
  "Berkebinekaan Global",
  "Bergotong-royong",
  "Mandiri",
  "Bernalar Kritis",
  "Kreatif",
];

export default function ManualGradeEditor({ student, onUpdated }) {
  const [openSem, setOpenSem] = useState(null);

  return (
    <Card data-testid="manual-grade-editor">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Pencil className="w-5 h-5 text-blue-700" /> Input Nilai Manual per Semester
        </CardTitle>
        <CardDescription>
          Alternatif dari impor Leger Excel. Klik semester untuk memasukkan / mengubah nilai. Semester 6 juga menampilkan blok Kelulusan pada PDF.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SEM_META.map((s) => {
            const sd = student.semesters?.[String(s.n)] || {};
            const count = (sd.grades || []).length;
            const filled = count > 0;
            return (
              <button
                key={s.n}
                onClick={() => setOpenSem(s.n)}
                data-testid={`open-sem-editor-${s.n}`}
                className={
                  "text-left p-3 rounded-lg border transition-all " +
                  (filled
                    ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                    : "border-slate-200 bg-white hover:bg-slate-50")
                }
              >
                <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Kelas {s.kelas} • {count > 0 ? `${count} mata pelajaran` : "Belum diisi"}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>

      {openSem !== null && (
        <SemesterEditorDialog
          student={student}
          sem={openSem}
          isSem6={openSem === 6}
          onClose={() => setOpenSem(null)}
          onSaved={() => {
            setOpenSem(null);
            onUpdated?.();
          }}
        />
      )}
    </Card>
  );
}

function SemesterEditorDialog({ student, sem, isSem6, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [attendance, setAttendance] = useState({ sakit: 0, izin: 0, alpa: 0 });
  const [p5, setP5] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/students/${student.id}/semester/${sem}`);
        setSubjects(data.subjects || []);
        // If empty grades, seed rows with default subjects
        if (!data.grades || data.grades.length === 0) {
          setGrades(
            (data.subjects || []).map((s) => ({
              mata_pelajaran: s,
              kkm: 75,
              nilai: "",
              predikat: "",
              deskripsi: "",
            }))
          );
        } else {
          setGrades(data.grades);
        }
        setAttendance({
          sakit: data.attendance?.sakit ?? 0,
          izin: data.attendance?.izin ?? 0,
          alpa: data.attendance?.alpa ?? 0,
        });
        setP5(data.p5 || {});
      } catch (err) {
        toast.error("Gagal memuat data semester");
      } finally {
        setLoading(false);
      }
    })();
  }, [student.id, sem]);

  const setGrade = (i, k, v) => setGrades((rows) => rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () =>
    setGrades((rows) => [...rows, { mata_pelajaran: "", kkm: 75, nilai: "", predikat: "", deskripsi: "" }]);
  const delRow = (i) => setGrades((rows) => rows.filter((_, idx) => idx !== i));

  const save = async () => {
    // filter out fully empty rows
    const clean = grades.filter((g) => (g.mata_pelajaran || "").trim() !== "");
    // coerce numeric nilai/kkm
    const norm = clean.map((g) => ({
      ...g,
      kkm: g.kkm === "" || g.kkm === null ? 75 : Number(g.kkm) || g.kkm,
      nilai: g.nilai === "" || g.nilai === null ? null : (isNaN(Number(g.nilai)) ? g.nilai : Number(g.nilai)),
    }));
    setSaving(true);
    try {
      await api.put(`/admin/students/${student.id}/semester/${sem}`, {
        grades: norm,
        attendance: {
          sakit: Number(attendance.sakit) || 0,
          izin: Number(attendance.izin) || 0,
          alpa: Number(attendance.alpa) || 0,
        },
        p5,
      });
      toast.success(`Nilai Semester ${sem} tersimpan`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid={`sem-editor-dialog-${sem}`}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Input Nilai Semester {sem} {isSem6 ? "(termasuk Kelulusan)" : ""}
          </DialogTitle>
          <DialogDescription>
            {student.nama_lengkap} • NISN {student.nisn} • Kelas {student.kelas || "-"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-700" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* P5 (only shown for editing on all semesters — official layout has it) */}
            <div>
              <div className="text-sm font-semibold text-slate-900 mb-2">
                A. Projek Penguatan Profil Pelajar Pancasila (P5)
              </div>
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="p-2 text-left">Dimensi</th>
                      <th className="p-2 text-center w-24">Nilai</th>
                      <th className="p-2 text-left">Target Pencapaian di Akhir Fase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {P5_DIMENSIONS.map((d, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="p-2 text-slate-900">{d}</td>
                        <td className="p-2">
                          <Input
                            value={p5[`nilai_${i + 1}`] || ""}
                            onChange={(e) => setP5((s) => ({ ...s, [`nilai_${i + 1}`]: e.target.value }))}
                            className="h-8 text-center"
                            placeholder="BB/MB/BSH/SB"
                            data-testid={`p5-nilai-${i + 1}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={p5[`target_${i + 1}`] || ""}
                            onChange={(e) => setP5((s) => ({ ...s, [`target_${i + 1}`]: e.target.value }))}
                            className="h-8"
                            placeholder="Deskripsi target"
                            data-testid={`p5-target-${i + 1}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Nilai gunakan: BB / MB / BSH / SB
              </div>
            </div>

            {/* Grades */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-900">
                  B. Pengetahuan & Keterampilan (Mata Pelajaran)
                </div>
                <Button size="sm" variant="outline" onClick={addRow} data-testid="add-grade-row">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Baris
                </Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="p-2 text-left">Mata Pelajaran</th>
                      <th className="p-2 text-center w-20">KKM</th>
                      <th className="p-2 text-center w-20">Nilai</th>
                      <th className="p-2 text-center w-24">Predikat</th>
                      <th className="p-2 text-left">Deskripsi (Capaian Pembelajaran)</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map((g, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="p-1.5">
                          <Input
                            value={g.mata_pelajaran || ""}
                            onChange={(e) => setGrade(i, "mata_pelajaran", e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`grade-mp-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={g.kkm ?? ""}
                            onChange={(e) => setGrade(i, "kkm", e.target.value)}
                            className="h-8 text-xs text-center"
                            data-testid={`grade-kkm-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={g.nilai ?? ""}
                            onChange={(e) => setGrade(i, "nilai", e.target.value)}
                            className="h-8 text-xs text-center"
                            data-testid={`grade-nilai-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Input
                            value={g.predikat || ""}
                            onChange={(e) => setGrade(i, "predikat", e.target.value)}
                            className="h-8 text-xs text-center"
                            placeholder="A/B+/..."
                            data-testid={`grade-predikat-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Textarea
                            rows={1}
                            value={g.deskripsi || ""}
                            onChange={(e) => setGrade(i, "deskripsi", e.target.value)}
                            className="text-xs min-h-[32px]"
                            data-testid={`grade-desc-${i}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => delRow(i)}
                            className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                            data-testid={`grade-del-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Attendance */}
            <div>
              <div className="text-sm font-semibold text-slate-900 mb-2">C. Ketidakhadiran (Hari)</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Sakit</Label>
                  <Input
                    type="number"
                    value={attendance.sakit}
                    onChange={(e) => setAttendance((a) => ({ ...a, sakit: e.target.value }))}
                    className="h-9 mt-1"
                    data-testid="att-sakit"
                  />
                </div>
                <div>
                  <Label className="text-xs">Izin</Label>
                  <Input
                    type="number"
                    value={attendance.izin}
                    onChange={(e) => setAttendance((a) => ({ ...a, izin: e.target.value }))}
                    className="h-9 mt-1"
                    data-testid="att-izin"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tanpa Keterangan (Alpa)</Label>
                  <Input
                    type="number"
                    value={attendance.alpa}
                    onChange={(e) => setAttendance((a) => ({ ...a, alpa: e.target.value }))}
                    className="h-9 mt-1"
                    data-testid="att-alpa"
                  />
                </div>
              </div>
            </div>

            {isSem6 && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                <strong>Catatan:</strong> Data Kelulusan (LULUS / TIDAK LULUS, No. SK, No. Ijazah, Tanggal, Rata-rata,
                Melanjutkan ke) diisi di bagian <em>Keputusan Kelulusan</em> pada halaman detail siswa. PDF Semester 6
                akan otomatis menampilkan blok Kelulusan.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={onClose} data-testid="sem-editor-cancel">
                Batal
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="sem-editor-save"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Simpan Semester {sem}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
